import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import {
  Search,
  Plane,
  User,
  Calendar,
  Clock,
  MapPin,
  Download,
  Loader2,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  FileText,
  Globe,
  Phone,
  Mail,
  Ticket
} from 'lucide-react';
import axios from 'axios';
import { downloadETicketPDF } from '../../utils/pdfGenerator';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const GDS_OPTIONS = [
  { value: 'sabre', label: 'Sabre', color: 'bg-red-500' },
  { value: 'travelport', label: 'Travelport', color: 'bg-blue-500' },
  { value: 'amadeus', label: 'Amadeus', color: 'bg-green-500' }
];

const AIRPORT_CITIES = {
  'LHR': 'London Heathrow', 'LGW': 'London Gatwick', 'STN': 'London Stansted',
  'LTN': 'London Luton', 'MAN': 'Manchester', 'BHX': 'Birmingham',
  'DXB': 'Dubai', 'DOH': 'Doha', 'AUH': 'Abu Dhabi', 'JED': 'Jeddah',
  'RUH': 'Riyadh', 'MED': 'Medina', 'ISB': 'Islamabad', 'LHE': 'Lahore',
  'KHI': 'Karachi', 'DEL': 'New Delhi', 'BOM': 'Mumbai', 'SAW': 'Istanbul Sabiha',
  'IST': 'Istanbul', 'ESB': 'Ankara', 'CAI': 'Cairo', 'AMM': 'Amman'
};

export const PNRRetrievalPanel = () => {
  const [pnr, setPnr] = useState('');
  const [ticketNumber, setTicketNumber] = useState('');
  const [selectedGds, setSelectedGds] = useState('sabre');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [booking, setBooking] = useState(null);
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('en-GB', { 
        weekday: 'short', 
        day: '2-digit', 
        month: 'short', 
        year: 'numeric' 
      });
    } catch {
      return dateStr;
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const clean = timeStr.split('+')[0].split('Z')[0];
    return clean.substring(0, 5);
  };

  const getCityName = (code) => AIRPORT_CITIES[code?.toUpperCase()] || code;

  const handleSearch = async () => {
    if (!pnr.trim() && !ticketNumber.trim()) {
      setError('Please enter a PNR or E-ticket number');
      return;
    }

    setLoading(true);
    setError('');
    setBooking(null);

    try {
      const response = await axios.post(`${API_URL}/api/gds/retrieve-pnr`, {
        pnr: pnr.trim().toUpperCase(),
        gds: selectedGds,
        ticket_number: ticketNumber.trim()
      });

      if (response.data.success) {
        setBooking(response.data.booking);
      } else {
        setError(response.data.message || 'PNR not found');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to retrieve PNR');
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!booking) return;
    
    setGeneratingPdf(true);
    try {
      // Prepare booking data for PDF generation
      const pdfBooking = {
        pnr: booking.pnr,
        passengers: booking.passengers || booking.local_data?.passengers || [],
        flight_data: {
          segments: booking.segments || [],
          return_segments: booking.return_segments || [],
          fare_details: booking.fare_details || booking.local_data?.flight_data?.fare_details || {},
          from: booking.segments?.[0]?.departure_airport || '',
          to: booking.segments?.[0]?.arrival_airport || '',
          departure_date: booking.segments?.[0]?.departure_date || '',
          departure_time: booking.segments?.[0]?.departure_time || '',
          arrival_time: booking.segments?.[0]?.arrival_time || ''
        },
        contact: booking.contact || booking.local_data?.contact || {},
        total_price: booking.fare_details?.total_price || booking.local_data?.total_price || 0,
        status: booking.status || 'CONFIRMED'
      };
      
      await downloadETicketPDF(pdfBooking);
    } catch (err) {
      console.error('PDF generation failed:', err);
      setError('Failed to generate PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  const handleReset = () => {
    setPnr('');
    setTicketNumber('');
    setBooking(null);
    setError('');
  };

  // Combine segments for display
  const allSegments = [
    ...(booking?.segments || []),
    ...(booking?.return_segments || [])
  ];

  return (
    <div className="space-y-6" data-testid="pnr-retrieval-panel">
      {/* Search Card */}
      <Card className="border-0 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-slate-800 to-slate-700 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            GDS PNR Retrieval
          </CardTitle>
          <p className="text-slate-300 text-sm mt-1">
            Retrieve booking details from Sabre, Travelport, or Amadeus
          </p>
        </CardHeader>
        <CardContent className="p-6">
          {/* GDS Selection */}
          <div className="mb-6">
            <Label className="text-sm font-medium text-slate-700 mb-2 block">Select GDS</Label>
            <div className="flex gap-3">
              {GDS_OPTIONS.map((gds) => (
                <button
                  key={gds.value}
                  onClick={() => setSelectedGds(gds.value)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    selectedGds === gds.value
                      ? `${gds.color} text-white shadow-md`
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`gds-${gds.value}`}
                >
                  {gds.label}
                </button>
              ))}
            </div>
          </div>

          {/* Search Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <Label htmlFor="pnr" className="text-sm font-medium text-slate-700 mb-2 block">
                PNR / Record Locator
              </Label>
              <Input
                id="pnr"
                value={pnr}
                onChange={(e) => setPnr(e.target.value.toUpperCase())}
                placeholder="e.g., DRCONF"
                className="font-mono text-lg tracking-wider"
                maxLength={6}
                data-testid="pnr-input"
              />
            </div>
            <div>
              <Label htmlFor="ticket" className="text-sm font-medium text-slate-700 mb-2 block">
                E-Ticket Number (Optional)
              </Label>
              <Input
                id="ticket"
                value={ticketNumber}
                onChange={(e) => setTicketNumber(e.target.value)}
                placeholder="e.g., 1234567890123"
                className="font-mono"
                data-testid="ticket-input"
              />
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button 
              onClick={handleSearch} 
              disabled={loading}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="search-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Retrieve PNR
                </>
              )}
            </Button>
            <Button 
              variant="outline" 
              onClick={handleReset}
              data-testid="reset-btn"
            >
              <RefreshCw className="h-4 w-4 mr-2" />
              Reset
            </Button>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-5 w-5" />
              {error}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Results */}
      {booking && (
        <div className="space-y-4" data-testid="booking-results">
          {/* Header with PNR and Status */}
          <Card className="border-0 shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-slate-800 to-slate-700 p-6 text-white">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-slate-400 text-sm">Booking Reference</p>
                  <h2 className="text-3xl font-bold tracking-wider text-sky-400">{booking.pnr}</h2>
                  <div className="flex items-center gap-2 mt-2">
                    <Badge className={`${
                      booking.source === 'sabre' ? 'bg-red-500' : 
                      booking.source === 'local_database' ? 'bg-purple-500' : 'bg-blue-500'
                    }`}>
                      {booking.source?.toUpperCase() || 'GDS'}
                    </Badge>
                    {booking.found_in_local_db && (
                      <Badge className="bg-emerald-500">In Local DB</Badge>
                    )}
                  </div>
                </div>
                <div className="text-right">
                  <Badge className="bg-emerald-500 text-white px-4 py-1">
                    <CheckCircle2 className="h-4 w-4 mr-1 inline" />
                    {booking.status || 'CONFIRMED'}
                  </Badge>
                  <Button 
                    onClick={handleDownloadPDF}
                    disabled={generatingPdf}
                    className="mt-3 bg-white text-slate-800 hover:bg-slate-100"
                    data-testid="download-pdf-btn"
                  >
                    {generatingPdf ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Download PDF
                  </Button>
                </div>
              </div>
            </div>
          </Card>

          {/* Passengers */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-slate-700">
                <User className="h-5 w-5 text-blue-500" />
                Passengers ({booking.passengers?.length || 0})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {(booking.passengers || []).map((pax, idx) => (
                  <div 
                    key={idx} 
                    className="flex items-center justify-between p-4 bg-slate-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-800">
                          {pax.title?.toUpperCase()} {pax.first_name?.toUpperCase()} {pax.last_name?.toUpperCase()}
                        </p>
                        <p className="text-sm text-slate-500">
                          {pax.type || 'ADULT'}
                          {pax.ticket_number && ` • Ticket: ${pax.ticket_number}`}
                        </p>
                      </div>
                    </div>
                    <Badge variant="outline" className="text-emerald-600 border-emerald-300">
                      <Ticket className="h-3 w-3 mr-1" />
                      E-TICKET
                    </Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Flight Itinerary */}
          <Card className="border-0 shadow-md">
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2 text-slate-700">
                <Plane className="h-5 w-5 text-blue-500" />
                Flight Itinerary ({allSegments.length} Segment{allSegments.length !== 1 ? 's' : ''})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {allSegments.map((seg, idx) => (
                  <div 
                    key={idx} 
                    className="border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-500 text-white rounded-lg flex items-center justify-center font-bold text-sm">
                          {idx + 1}
                        </div>
                        <div>
                          <span className="font-bold text-slate-800">
                            {seg.marketing_airline} {seg.flight_number}
                          </span>
                          {seg.operating_airline && seg.operating_airline !== seg.marketing_airline && (
                            <span className="text-slate-500 text-sm ml-2">
                              (Operated by {seg.operating_airline})
                            </span>
                          )}
                        </div>
                      </div>
                      <Badge className={`${seg.status === 'HK' ? 'bg-emerald-500' : 'bg-amber-500'}`}>
                        {seg.status || 'CONFIRMED'}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      {/* Departure */}
                      <div className="text-center">
                        <p className="text-2xl font-bold text-slate-800">
                          {formatTime(seg.departure_time)}
                        </p>
                        <p className="text-lg font-semibold text-blue-600">
                          {seg.departure_airport}
                        </p>
                        <p className="text-xs text-slate-500">
                          {getCityName(seg.departure_airport)}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          {formatDate(seg.departure_date)}
                        </p>
                      </div>

                      {/* Arrow */}
                      <div className="flex flex-col items-center justify-center">
                        <div className="w-full h-0.5 bg-gradient-to-r from-blue-500 to-emerald-500 relative">
                          <Plane className="h-5 w-5 text-blue-500 absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white" />
                        </div>
                        {seg.equipment && (
                          <p className="text-xs text-slate-400 mt-1">
                            Aircraft: {seg.equipment}
                          </p>
                        )}
                        {seg.booking_class && (
                          <p className="text-xs text-slate-400">
                            Class: {seg.booking_class}
                          </p>
                        )}
                      </div>

                      {/* Arrival */}
                      <div className="text-center">
                        <p className="text-2xl font-bold text-slate-800">
                          {formatTime(seg.arrival_time)}
                        </p>
                        <p className="text-lg font-semibold text-emerald-600">
                          {seg.arrival_airport}
                        </p>
                        <p className="text-xs text-slate-500">
                          {getCityName(seg.arrival_airport)}
                        </p>
                        <p className="text-sm text-slate-600 mt-1">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          {formatDate(seg.arrival_date || seg.departure_date)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          {/* Fare Details & Contact */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Fare Details */}
            {booking.fare_details && (
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-700">
                    <FileText className="h-5 w-5 text-blue-500" />
                    Fare Details
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between">
                      <span className="text-slate-600">Base Fare</span>
                      <span className="font-medium">
                        £{(booking.fare_details.base_fare || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-600">Taxes & Fees</span>
                      <span className="font-medium">
                        £{(booking.fare_details.total_tax || 0).toFixed(2)}
                      </span>
                    </div>
                    <div className="border-t pt-2 flex justify-between">
                      <span className="font-semibold text-slate-800">Total</span>
                      <span className="font-bold text-xl text-emerald-600">
                        £{(booking.fare_details.total_price || booking.local_data?.total_price || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contact Info */}
            {(booking.contact?.email || booking.contact?.phone) && (
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-lg flex items-center gap-2 text-slate-700">
                    <User className="h-5 w-5 text-blue-500" />
                    Contact Information
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {booking.contact?.email && (
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-slate-400" />
                        <span className="text-slate-700">{booking.contact.email}</span>
                      </div>
                    )}
                    {booking.contact?.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="h-5 w-5 text-slate-400" />
                        <span className="text-slate-700">{booking.contact.phone}</span>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default PNRRetrievalPanel;
